/**
 * errorReporter
 *
 * Authenticated automatic error reporting. Wraps console.error,
 * window.error, and unhandledrejection to fire deduplicated reports
 * to the report-auto-error edge function.
 *
 * Recursion-safe: an in-flight flag suppresses re-entry from the
 * console.error wrap when the reporter's own machinery surfaces an
 * error. Wrapper composition uses the *current* console.error at init
 * time as the passthrough, so consoleLogBuffer (which must be init'd
 * before this module) continues to capture entries.
 */

import { supabase } from '@/utils/supabase';
import { captureEnvironment } from '@/utils/feedbackEnvironment';
import type { ErrorType } from '@/types/feedback';

let initialized = false;
let inFlight = false;
const seen = new Set<string>();
const SEEN_TTL_MS = 60_000;
const MAX_REPORTS_PER_PAGE = 20;
let totalReports = 0;

interface AutoErrorReportPayload {
  error_hash: string;
  error_message: string;
  error_type: ErrorType;
  stack_trace?: string;
  environment: ReturnType<typeof captureEnvironment>;
  url: string;
}

function functionsHttpStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const context = (error as { context?: unknown }).context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  const status = (context as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

export async function invokeAutoErrorReport(
  payload: AutoErrorReportPayload
): Promise<void> {
  const firstAttempt = await supabase.functions.invoke('report-auto-error', {
    body: payload
  });
  if (functionsHttpStatus(firstAttempt.error) !== 401) {
    return;
  }

  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session?.access_token) {
    return;
  }

  await supabase.functions.invoke('report-auto-error', {
    body: payload,
    headers: {
      Authorization: `Bearer ${data.session.access_token}`
    }
  });
}

export function initErrorReporter(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  // Capture whatever console.error is NOW — i.e., consoleLogBuffer's wrap —
  // so its buffer continues to capture entries downstream.
  const passthrough = console.error;
  console.error = (...args: unknown[]) => {
    if (!inFlight) {
      // Don't await — fire and forget. Recursion guard prevents loops.
      void report('console_error', argsToString(args), captureStackHint());
    }
    passthrough.apply(console, args);
  };

  window.addEventListener('error', (e: ErrorEvent) => {
    void report('js_exception', e.message ?? 'Unknown error', e.error?.stack);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const r = e.reason as { message?: string; stack?: string } | string | undefined;
    const message = typeof r === 'string' ? r : (r?.message ?? String(r));
    const stack = typeof r === 'object' && r ? r.stack : undefined;
    void report('unhandled_rejection', message, stack);
  });
}

async function report(type: ErrorType, message: string, stack?: string): Promise<void> {
  if (totalReports >= MAX_REPORTS_PER_PAGE || inFlight) {
    return;
  }

  let hash: string;
  try {
    hash = await sha256Hex(
      `${type}|${(message.split('\n')[0] || '').slice(0, 200)}|${(stack?.split('\n')[1] || '').trim()}`
    );
  } catch {
    return;
  }

  if (seen.has(hash)) {
    return;
  }
  seen.add(hash);
  setTimeout(() => seen.delete(hash), SEEN_TTL_MS);
  totalReports += 1;

  inFlight = true;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return;
    }

    await invokeAutoErrorReport({
      error_hash: hash,
      error_message: message,
      error_type: type,
      stack_trace: stack,
      environment: captureEnvironment(),
      url: scrubUrl(window.location.href)
    });
  } catch {
    // Swallow — never call console.error from the catch branch.
  } finally {
    inFlight = false;
  }
}

function argsToString(args: unknown[]): string {
  return args
    .map((a) => {
      if (a instanceof Error) {
        return a.stack ?? a.message ?? String(a);
      }
      if (typeof a === 'string') {
        return a;
      }
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

function captureStackHint(): string | undefined {
  // Best-effort: synthesize a stack at the call site.
  return new Error().stack;
}

function scrubUrl(href: string): string {
  try {
    const u = new URL(href);
    return `${u.origin}${u.pathname}`;
  } catch {
    return href;
  }
}

async function sha256Hex(s: string): Promise<string> {
  const enc = new TextEncoder().encode(s);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
